import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Portal } from "~/helpers/portal";
import { useAccount } from "~/hooks/useAccount";
import { skipTrialDiscount } from "~/api/billing/requests";
import { DISABLE_PAYMENTS } from "~/helpers/plans";
import { excludePaths } from "~/constants/paths";
import { toast } from "~/components/toast";
import { faCreditCard } from "@fortawesome/pro-solid-svg-icons";
import { SVG } from "~/components/ui/SVG";
import * as S from "./styles";

const CLOSE_DELAY_MS = 60_000; // 1 minute before close button becomes active
const DISMISSED_KEY = "skip_trial_banner_dismissed";

export const SkipTrialBanner = () => {
  const { t } = useTranslation();
  const { account, refetch } = useAccount();
  const [canClose, setCanClose] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    return sessionStorage.getItem(DISMISSED_KEY) === "true";
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const mountTimeRef = useRef(Date.now());

  useEffect(() => {
    const elapsed = Date.now() - mountTimeRef.current;
    const remaining = Math.max(CLOSE_DELAY_MS - elapsed, 0);
    const timer = setTimeout(() => setCanClose(true), remaining);
    return () => clearTimeout(timer);
  }, []);

  const isTrialing = useCallback(() => {
    if (!account?.active_subscription) return false;
    if (!account.active_subscription.trial_end_at) return false;
    return new Date(account.active_subscription.trial_end_at) > new Date();
  }, [account]);

  const shouldShow = (() => {
    if (DISABLE_PAYMENTS) return false;
    if (dismissed) return false;
    if (!account) return false;
    if (account.has_redeemed_skip_trial_coupon) return false;
    if (account.payment_provider !== "stripe" && account.active_subscription?.payment_provider !== "stripe") return false;
    if (!isTrialing()) return false;
    if (excludePaths.some((path) => window.location.pathname.includes(path)))
      return false;
    return true;
  })();

  const handleClose = () => {
    if (!canClose) return;
    setDismissed(true);
    sessionStorage.setItem(DISMISSED_KEY, "true");
  };

  const handleClaimClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      await skipTrialDiscount();
      setShowConfirmModal(false);
      setDismissed(true);
      sessionStorage.setItem(DISMISSED_KEY, "true");
      toast.success(t("skip_trial.success_toast"));
      await refetch();
    } catch (err: any) {
      const message =
        err?.response?.data?.error || "Something went wrong. Please try again.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const cardDigits = account?.stripe_card_digits;

  if (!shouldShow) return null;

  return (
    <>
      {/* Sticky Banner - bottom left */}
      {!showConfirmModal && (
        <Portal>
          <S.BannerWrapper>
            <S.CloseButton
              disabled={!canClose}
              onClick={handleClose}
              aria-label="Close"
              title={
                canClose
                  ? "Close"
                  : "Close button will be available shortly"
              }>
              &#x2715;
            </S.CloseButton>
            <S.BannerTitle>{t("skip_trial.banner_title")}</S.BannerTitle>
            <S.BannerDescription>
              {t("skip_trial.banner_description")}
            </S.BannerDescription>
            <S.ClaimButton onClick={handleClaimClick}>
              {t("skip_trial.claim_discount")}
            </S.ClaimButton>
          </S.BannerWrapper>
        </Portal>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <Portal>
          <S.ModalOverlay
            role="dialog"
            aria-modal="true"
            aria-labelledby="skip-trial-confirm-title"
            aria-describedby="skip-trial-confirm-desc">
            <S.ModalCard>
              <S.ModalCloseButton
                onClick={() => setShowConfirmModal(false)}
                aria-label="Close">
                &#x2715;
              </S.ModalCloseButton>

              <S.ModalTitle id="skip-trial-confirm-title">
                {t("skip_trial.confirm_title")}
              </S.ModalTitle>

              <S.ModalDescription id="skip-trial-confirm-desc">
                {t("skip_trial.confirm_description")}
              </S.ModalDescription>

              {/* Testimonial */}
              <S.TestimonialBox>
                <S.TestimonialText>
                  {t("skip_trial.testimonial_text")}
                </S.TestimonialText>
              </S.TestimonialBox>

              {/* Card on file */}
              {cardDigits && (
                <S.CardRow>
                  <S.CardIcon>
                    <SVG icon={faCreditCard} size="lg" />
                  </S.CardIcon>
                  <div>
                    <S.CardLabel>{t("skip_trial.card_label")}</S.CardLabel>
                    <S.CardDigits>**** **** **** {cardDigits}</S.CardDigits>
                  </div>
                  <S.ChangeCardLink href="/settings/account">
                    Change card
                  </S.ChangeCardLink>
                </S.CardRow>
              )}

              <S.ModalActions>
                <S.ConfirmButton
                  onClick={handleConfirm}
                  disabled={isSubmitting}>
                  {isSubmitting
                    ? "Processing..."
                    : t("skip_trial.confirm_button")}
                </S.ConfirmButton>
              </S.ModalActions>
            </S.ModalCard>
          </S.ModalOverlay>
        </Portal>
      )}
    </>
  );
};
